import { useState } from 'react';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import type { ContentI18n, SurveyLang, SurveyTranslatableLang } from '@/shared/types/nps';
import {
  SURVEY_LANG_LABEL,
  SURVEY_TRANSLATABLE_LANGS,
  type ContentI18nUpdater,
} from '../../model/content-i18n';

const LANG_OPTIONS: Array<{ value: SurveyLang; label: string }> = [
  { value: 'pt-BR', label: 'BR' },
  ...SURVEY_TRANSLATABLE_LANGS.map((lng) => ({
    value: lng as SurveyLang,
    label: lng === 'pt-PT' ? 'PT' : lng.toUpperCase(),
  })),
];

interface Props {
  titleValue: string;
  descriptionValue: string;
  i18nValue?: ContentI18n | null;
  onTitleChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onI18nChange: (update: ContentI18nUpdater) => void;
  disabled?: boolean;
  titleError?: string;
  descriptionError?: string;
  /** Usa Textarea na descrição (survey) ou Input (pergunta). */
  descriptionAsTextarea?: boolean;
  titleLabel?: string;
  descriptionLabel?: string;
  titlePlaceholder?: string;
  descriptionPlaceholder?: string;
}

/** Campos título/descrição com seletor de idioma (pt-BR canônico + i18n). */
export function ContentI18nFields({
  titleValue,
  descriptionValue,
  i18nValue,
  onTitleChange,
  onDescriptionChange,
  onI18nChange,
  disabled,
  titleError,
  descriptionError,
  descriptionAsTextarea = true,
  titleLabel = 'Título',
  descriptionLabel = 'Descrição',
  titlePlaceholder,
  descriptionPlaceholder,
}: Props) {
  const [lang, setLang] = useState<SurveyLang>('pt-BR');
  const isCanonical = lang === 'pt-BR';
  const entry = !isCanonical ? i18nValue?.[lang as SurveyTranslatableLang] : undefined;

  const currentTitle = isCanonical ? titleValue : (entry?.title ?? '');
  const currentDescription = isCanonical ? descriptionValue : (entry?.description ?? '');

  const setLocalized = (patch: { title?: string; description?: string }) => {
    if (isCanonical) {
      if (patch.title !== undefined) onTitleChange(patch.title);
      if (patch.description !== undefined) onDescriptionChange(patch.description);
      return;
    }
    const key = lang as SurveyTranslatableLang;
    // Sempre via updater: não usar snapshot de `i18nValue` (pode estar stale vs form store).
    onI18nChange((prev) => ({
      ...(prev ?? {}),
      [key]: {
        ...(prev?.[key] ?? {}),
        ...patch,
      },
    }));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <Label>Idioma do conteúdo</Label>
          <SegmentedControl
            size="sm"
            fill
            value={lang}
            onChange={setLang}
            className="sm:w-auto sm:[&_button]:flex-none sm:[&_button]:px-3"
            options={LANG_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`content-title-${lang}`}>
          {titleLabel}
          {isCanonical ? '' : ` (${SURVEY_LANG_LABEL[lang]})`}
        </Label>
        <Input
          id={`content-title-${lang}`}
          value={currentTitle}
          disabled={disabled}
          placeholder={titlePlaceholder}
          onChange={(e) => setLocalized({ title: e.target.value })}
        />
        {isCanonical && titleError ? (
          <p className="text-xs text-destructive">{titleError}</p>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`content-desc-${lang}`}>
          {descriptionLabel}
          {isCanonical ? '' : ` (${SURVEY_LANG_LABEL[lang]})`}
        </Label>
        {descriptionAsTextarea ? (
          <Textarea
            id={`content-desc-${lang}`}
            rows={2}
            disabled={disabled}
            placeholder={descriptionPlaceholder}
            value={currentDescription}
            onChange={(e) => setLocalized({ description: e.target.value })}
          />
        ) : (
          <Input
            id={`content-desc-${lang}`}
            disabled={disabled}
            placeholder={descriptionPlaceholder}
            value={currentDescription}
            onChange={(e) => setLocalized({ description: e.target.value })}
          />
        )}
        {isCanonical && descriptionError ? (
          <p className="text-xs text-destructive">{descriptionError}</p>
        ) : null}
      </div>
    </div>
  );
}
